import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import PaymentSection from './PaymentSection';

/**
 * PaymentConfirmModal
 *
 * Opens after the user clicks "Checkout" on the billing form.
 * Step 1 — payment entry (PaymentSection)
 * Step 2 — send confirmation (Email / WhatsApp / Both / Skip)
 *
 * Props:
 *   isOpen            {bool}
 *   onClose           {fn}       — closes modal; parent clears form
 *   grandTotal        {number|string}
 *   currency          {string}   — 'INR' | 'USD'
 *   customerId        {number|null}
 *   dollarRate        {number|null}
 *   goldRate          {number|null}
 *   customerName      {string}
 *   customerPhone     {string}
 *   customerEmail     {string}
 *   onSaveAndConfirm  {fn(payments) → Promise<savedBilling|null>}
 *   onSendEmail       {fn(savedBilling) → Promise<void>}
 *   onSendWhatsApp    {fn(savedBilling) → Promise<void>}
 */
export default function PaymentConfirmModal({
  isOpen,
  onClose,
  grandTotal = 0,
  currency = 'INR',
  customerId = null,
  dollarRate = null,
  goldRate = null,
  customerName = '',
  customerPhone = '',
  customerEmail = '',
  onSaveAndConfirm,
  onSendEmail,
  onSendWhatsApp,
}) {
  const [payments, setPayments] = useState([]);
  const [step, setStep] = useState('payment'); // 'payment' | 'confirmation'
  const [savedBilling, setSavedBilling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sent, setSent] = useState({ email: false, whatsapp: false });

  if (!isOpen) return null;

  const grand = parseFloat(grandTotal) || 0;
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balance = Math.max(0, grand - totalPaid);
  const isFullyPaid = totalPaid >= grand - 0.01 && totalPaid > 0;
  const isPartial = totalPaid > 0 && !isFullyPaid;
  const currSym = currency === 'USD' ? '$' : '₹';
  const fmt = (v) => v.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleConfirm = async () => {
    if (totalPaid <= 0) {
      alert('Please add at least one payment before confirming.');
      return;
    }
    setSaving(true);
    const result = await onSaveAndConfirm(payments);
    setSaving(false);
    if (result) {
      setSavedBilling(result);
      setStep('confirmation');
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    await onSendEmail(savedBilling);
    setSendingEmail(false);
    setSent(s => ({ ...s, email: true }));
  };

  const handleSendWhatsApp = async () => {
    setSendingWhatsApp(true);
    await onSendWhatsApp(savedBilling);
    setSendingWhatsApp(false);
    setSent(s => ({ ...s, whatsapp: true }));
  };

  const handleSendBoth = async () => {
    setSendingEmail(true);
    setSendingWhatsApp(true);
    await Promise.all([onSendEmail(savedBilling), onSendWhatsApp(savedBilling)]);
    setSendingEmail(false);
    setSendingWhatsApp(false);
    setSent({ email: true, whatsapp: true });
  };

  const handleClose = () => {
    const wasSaved = !!savedBilling;
    setPayments([]);
    setStep('payment');
    setSavedBilling(null);
    setSent({ email: false, whatsapp: false });
    onClose(wasSaved);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-800">
            {step === 'payment' ? 'Payment' : 'Bill Confirmed'}
          </h2>
          <button onClick={handleClose} className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {step === 'payment' && (
            <PaymentSection
              grandTotal={grand}
              currency={currency}
              customerId={customerId}
              dollarRate={dollarRate}
              goldRate={goldRate}
              onPaymentsChange={setPayments}
            />
          )}

          {step === 'confirmation' && savedBilling && (
            <div className="space-y-5">
              {/* Success banner */}
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    Bill #{savedBilling.bill_number} saved
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {isFullyPaid
                      ? `Fully paid — ${currSym}${fmt(totalPaid)}`
                      : isPartial
                        ? `Partial payment — ${currSym}${fmt(totalPaid)} received · Balance due: ${currSym}${fmt(balance)}`
                        : 'No payment recorded'}
                  </p>
                </div>
              </div>

              {/* Send confirmation */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Send receipt to customer?</p>
                <div className="grid grid-cols-2 gap-3">

                  {/* Email */}
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || sendingWhatsApp || !customerEmail}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition
                      disabled:opacity-40
                      bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                  >
                    {sendingEmail
                      ? <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      : sent.email ? '✓' : '📧'}
                    {sent.email ? 'Email Sent' : !customerEmail ? 'No email on file' : 'Send Email'}
                  </button>

                  {/* WhatsApp */}
                  <button
                    onClick={handleSendWhatsApp}
                    disabled={sendingWhatsApp || sendingEmail || !customerPhone}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition
                      disabled:opacity-40
                      bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  >
                    {sendingWhatsApp
                      ? <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      : sent.whatsapp ? '✓' : '💬'}
                    {sent.whatsapp ? 'WhatsApp Sent' : !customerPhone ? 'No phone on file' : 'Send WhatsApp'}
                  </button>

                  {/* Both */}
                  {!sent.email && !sent.whatsapp && customerEmail && customerPhone && (
                    <button
                      onClick={handleSendBoth}
                      disabled={sendingEmail || sendingWhatsApp}
                      className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition
                        disabled:opacity-40 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                    >
                      📧💬 Send Both
                    </button>
                  )}
                </div>

                {!customerEmail && !customerPhone && (
                  <p className="text-xs text-slate-400 italic mt-3">No contact details on file — add email or phone to enable sending.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
          {step === 'payment' ? (
            <>
              <div className="text-xs text-slate-500">
                Grand Total: <span className="font-semibold text-slate-700">{currSym}{fmt(grand)}</span>
                {totalPaid > 0 && (
                  <span className="ml-3">
                    {isFullyPaid
                      ? <span className="text-green-600 font-semibold">Fully Paid</span>
                      : <span className="text-orange-600">Balance: {currSym}{fmt(balance)}</span>}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={handleClose} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={saving || totalPaid <= 0}
                  className="px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 transition flex items-center gap-2"
                >
                  {saving && <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                  {isFullyPaid ? 'Confirm Payment' : isPartial ? 'Save Partial Payment' : 'Confirm Payment'}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="ml-auto px-6 py-2 text-sm font-semibold rounded-lg bg-slate-700 hover:bg-slate-800 text-white transition"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
